import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { getAppointmentPatients } from "services/api/taptype/patients";
import {
	apiHandler,
	calculateAgeFromBirthDate,
	errorResponseInterceptor,
	successResponseInterceptor,
} from "utils/helpers";
import { useSkyflow } from "utils/skyflowProxyContainers";
import vars from "utils/vars";
import { default as AppointmentPatientFilterForm } from "views/forms/AppointmentPatientFilter";
import NoData from "views/sections/NoData";
import { object, string } from "yup";

const AppointmentPatientsList = ({ onSelect }) => {
	const { t, i18n } = useTranslation();
	const skyFlowSingleContainer = useSkyflow();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// constants
	const patientQueryParametersOptions = [
		{ value: "name", title: "Patient Name" },
		{ value: "health_card_number", title: "HCN" },
		{ value: "mobile", title: "Mobile" },
		{ value: "email", title: "Email" },
		{ value: "date_of_birth", title: "DOB" },
	];

	// ref hook
	const appointmentPatientsCancelRequestRef = useRef(null);

	// state hook
	const [patientsState, setPatientsState] = useState([]);
	const [paginationState, setPaginationState] = useState({});
	const [loadMorePatientsLoadingState, setLoadMorePatientsLoadingState] = useState(false);
	const [isLoadingState, setIsLoadingState] = useState(false);

	// event handlers
	const getPatientsAPIHandler = useCallback(
		async (query, { setErrors, isPagination }) => {
			if (isPagination) setLoadMorePatientsLoadingState(true);
			if (!isPagination) {
				setIsLoadingState(true);
				setPatientsState(() => []);
				setPaginationState(() => ({}));
			}

			if (appointmentPatientsCancelRequestRef.current?.signal)
				appointmentPatientsCancelRequestRef.current?.abort();
			appointmentPatientsCancelRequestRef.current = new AbortController();

			await apiHandler(getAppointmentPatients, {
				data: {
					query,
					options: { signal: appointmentPatientsCancelRequestRef.current.signal },
				},
				onError: ({ errors }) => {
					if (errors) setErrors(errors);
					if (isPagination) setLoadMorePatientsLoadingState(false);
					if (!isPagination) setIsLoadingState(false);
				},
				onSuccess: async (response) => {
					setPaginationState(response?.meta?.pagination || {});

					const notePatientsIds = [...(query?.query_value ? response?.data || [] : [])];

					if (!notePatientsIds.length) {
						if (isPagination) setLoadMorePatientsLoadingState(false);
						if (!isPagination) setIsLoadingState(false);
						if (!query?.query_value) setPatientsState(() => []);
						return;
					}

					await apiHandler(
						skyFlowSingleContainer.get({
							records: [
								{
									table: vars.app.skyflowTables.users,
									ids: notePatientsIds,
								},
							],
						}),
						{
							onError: (responseError) => {
								if (isPagination) setLoadMorePatientsLoadingState(false);
								if (!isPagination) setIsLoadingState(false);
								setPatientsState((prevState) => [
									...(isPagination ? prevState : []),
									...(response?.data || []),
								]);
								setPaginationState(response?.meta?.pagination || {});
								const { errors = [] } = responseError;
								const statusCodes = [...(errors?.map((err) => err?.error?.code || "") || [])].filter(
									Boolean
								);
								if (statusCodes?.some((item) => [404, 400]?.includes(item))) return;
								errorResponseInterceptor(responseError, {
									dispatch,
									navigate,
									language: i18n.language,
									t,
								});
							},
							onSuccess: (patientsResponse = {}) => {
								const patients =
									[...(patientsResponse?.records || [])].map((singlePatient) => {
										const { email, email_address, ...patient } = singlePatient?.fields || {};
										return {
											...(patient || {}),
											...((email_address || email) && {
												email: email_address || email || "",
											}),
										};
									}) || [];
								successResponseInterceptor(response, { dispatch, language: i18n.language, t });
								if (isPagination) setLoadMorePatientsLoadingState(false);
								if (!isPagination) setIsLoadingState(false);
								setPatientsState((prevState) => [
									...(isPagination ? prevState : []),
									...(patients || []),
								]);
							},
						}
					);
				},
			});
		},
		[dispatch, i18n.language, skyFlowSingleContainer, t, navigate]
	);
	const onFilterFormikSubmitHandler = async (values, actions) => getPatientsAPIHandler(values, actions);
	const onReadMoreButtonClickHandler = useCallback(
		async (values) => getPatientsAPIHandler(values, { isPagination: true }),
		[getPatientsAPIHandler]
	);

	// formik
	const {
		submitForm: filterFormikSubmitForm,
		resetForm: filterFormikResetForm,
		...filterFormik
	} = useFormik({
		initialValues: { query_parameter: patientQueryParametersOptions[0].value, query_value: null },
		validationSchema: object().shape({ query_value: string().required(t("system_messages.required_field")) }),
		onSubmit: onFilterFormikSubmitHandler,
	});

	// effect hooks
	// NOTE: Uncomment this if you want patients list to rendered on mount.
	// useEffect(() => {
	// 	filterFormikSubmitForm();
	// }, [filterFormikSubmitForm]);

	useEffect(() => {
		return () => {
			if (appointmentPatientsCancelRequestRef.current?.signal)
				appointmentPatientsCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<div className="appointment-patients-listing">
			<div className="row gy-3">
				<div className="col-12">
					<AppointmentPatientFilterForm
						formState={{
							submitForm: filterFormikSubmitForm,
							resetForm: filterFormikResetForm,
							...filterFormik,
						}}
						onSelect={onSelect}
						patientQueryParametersOptions={patientQueryParametersOptions}
					/>
				</div>
				<div className="col-12">
					{Boolean(filterFormik?.values?.query_value) ? (
						<>
							{[...(isLoadingState ? Array.apply(null, Array(5)).map((x, i) => i) : patientsState)]
								?.length === 0 ? (
								<NoData title={t("titles.no_patients_found")} headingLevel="h5" />
							) : (
								<div className="appointment-patients-listing-data overflow-y-auto overflow-x-hidden customized-scroll">
									<div className="row gy-4 justify-content-center">
										<div className="col-12">
											<div className="list-group list-group-flush">
												<div className="list-group-item bg-transparent border-0 p-2">
													<div className="row gx-1 flex-nowrap">
														<div className="col-3">
															<p className="text-capitalize mb-0">
																<strong>name</strong>
															</p>
														</div>
														<div className="col-2">
															<p className="text-capitalize mb-0">
																<strong>HCN</strong>
															</p>
														</div>
														<div className="col-1">
															<p className="text-capitalize mb-0">
																<strong>gen</strong>
															</p>
														</div>
														<div className="col-1">
															<p className="text-capitalize mb-0">
																<strong>age</strong>
															</p>
														</div>
														<div className="col-3">
															<p className="text-capitalize mb-0">
																<strong>email</strong>
															</p>
														</div>
														<div className="col-2">
															<p className="text-capitalize mb-0">
																<strong>phone</strong>
															</p>
														</div>
													</div>
												</div>
												{[
													...(isLoadingState
														? Array.apply(null, Array(5)).map((x, i) => i)
														: patientsState),
												]?.map((patient, i) => {
													const patientAge =
														(patient?.date_of_birth &&
															calculateAgeFromBirthDate(patient?.date_of_birth)) ||
														"";
													return (
														<button
															type="button"
															className="list-group-item list-group-item-action text-primary-500 p-2 rounded-2 my-1 border-0 small"
															disabled={isLoadingState}
															onClick={() => !isLoadingState && onSelect(patient)}
															key={patient?.id || i}>
															<span className="row gx-1 flex-nowrap">
																<span className="col-3">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{[
																				patient?.first_name || "",
																				patient?.last_name || "",
																			]
																				.filter(Boolean)
																				.join(" ")
																				.trim() || "-"}
																		</span>
																	)}
																</span>
																<span className="col-2">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{patient?.health_card_number || "-"}
																		</span>
																	)}
																</span>
																<span className="col-1">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{patient?.gender || "-"}
																		</span>
																	)}
																</span>
																<span className="col-1">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{patientAge?.years ? (
																				<span>{`${patientAge.years}Y`}</span>
																			) : patientAge?.months ? (
																				<span>{`${patientAge.months}M`}</span>
																			) : patientAge?.days ? (
																				<span>{`${patientAge.days}D`}</span>
																			) : (
																				<>-</>
																			)}
																		</span>
																	)}
																</span>
																<span className="col-3">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{patient?.email || "-"}
																		</span>
																	)}
																</span>
																<span className="col-2">
																	{isLoadingState ? (
																		<span className="text-capitalize mb-0 placeholder-glow d-block">
																			<span className="d-block placeholder bg-secondary w-100"></span>
																		</span>
																	) : (
																		<span className="text-capitalize d-block text-truncate mb-0">
																			{patient?.mobile || "-"}
																		</span>
																	)}
																</span>
															</span>
														</button>
													);
												})}
											</div>
										</div>
										{isLoadingState ? (
											<button
												tabIndex="-1"
												className="btn btn-link btn-sm disabled placeholder-glow d-flex justify-content-center align-items-center col-6 col-md-4 col-lg-6 col-xl-4"
												aria-hidden="true">
												<strong className="placeholder placeholder-sm bg-primary w-50">
													&nbsp;
												</strong>
											</button>
										) : (
											(Boolean(
												patientsState?.length &&
													paginationState?.total_pages > 1 &&
													+paginationState?.current_page < paginationState?.total_pages
											) && (
												<div className="col-6 col-md-4 col-lg-6 col-xl-4">
													<button
														type="button"
														className="btn btn-link btn-sm w-100 text-uppercase"
														onClick={() =>
															onReadMoreButtonClickHandler({
																...filterFormik.values,
																page: +paginationState?.current_page + 1,
															})
														}
														disabled={loadMorePatientsLoadingState}>
														{loadMorePatientsLoadingState ? (
															<span
																className="spinner-border spinner-border-sm"
																role="status">
																<span className="visually-hidden">{`${t(
																	"content.loading"
																)}...`}</span>
															</span>
														) : (
															<strong className="me-2">{t("actions.load_more")}</strong>
														)}
													</button>
												</div>
											)) || <></>
										)}
									</div>
								</div>
							)}
						</>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};

export default AppointmentPatientsList;
